import { defineStore } from 'pinia'
import { has, isEmpty } from 'lodash'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import * as SentryVue from '@sentry/vue'

import { api } from '@/services'
import authConstants from '@/constants/auth'
import { addCypressWindowListener } from '@/composables/cypress'
import { useAppStore } from '@/stores/app'
import { usePlatformAuthStore } from '@/stores/platformAuth'
import { useAuthHelpers } from '@/composables'

export const usePlatformAuthUrlStore = defineStore('platform-auth-url', {
    state: () => {
        return {
            browserAuthLoading: false,
            completionStarted: false,
        }
    },
    getters: {
        shouldUseAppViewerUrlAuthProcess() {
            if (Capacitor.isNativePlatform()) {
                return false
            }

            if (!window.Cypress) {
                return true
            }

            const initialSearchParams = new URLSearchParams(useAppStore().initialUrlSearch)
            return initialSearchParams.has('mock_app_viewer')
        },
    },
    actions: {
        start({ url }) {
            if (this.browserAuthLoading) {
                console.log('Browser auth already started, avoid calling again to avoid issues')
                return
            }

            const platformAuthStore = usePlatformAuthStore()

            this.completionStarted = false
            this.browserAuthLoading = true

            api.post(url)
                .then(({ data }) => {
                    if (!has(data, 'url')) {
                        throw new Error('Missing URL from start auth request')
                    }
                    const url = data.url

                    const handleBrowserFinished = () => {
                        Browser.removeAllListeners()
                        if (this.completionStarted) {
                            return
                        }
                        this.browserAuthLoading = false
                        platformAuthStore.events.emit('abort')
                    }

                    Browser.addListener('browserFinished', handleBrowserFinished)
                    addCypressWindowListener('Browser', 'browserFinished', handleBrowserFinished)

                    console.log('Opening browser from auth', url)
                    Browser.open({ url })
                        .catch((e) => {
                            this.browserAuthLoading = false
                            console.log('Unable to open browser from auth', url, e)
                            SentryVue.captureException(e)
                        })
                })
                .catch((error) => {
                    this.browserAuthLoading = false
                    platformAuthStore.events.emit('error', error)
                })
        },
        complete({ url }) {
            const authHelpers = useAuthHelpers()

            this.completionStarted = true
            // Remove browser finished event etc.
            Browser.removeAllListeners()
            // Close the in-app browser
            Browser.close()
                .catch((e) => console.log(e))

            if (!url) {
                this.browserAuthLoading = false
                this.completionStarted = false
                throw new Error('Missing complete URL')
            }

            const platformAuthStore = usePlatformAuthStore()

            return api.post(url)
                .then((response) => {
                    this.browserAuthLoading = false
                    authHelpers.handleUserContextChange(response.data)
                    platformAuthStore.events.emit('login', {
                        userSwitched: !isEmpty(response.headers[authConstants.LoginHeaders.userSwitched]),
                    })
                })
                .catch((error) => {
                    this.browserAuthLoading = false
                    this.completionStarted = false
                    platformAuthStore.events.emit('error', error)
                    throw new Error('Could not complete auth', error)
                })
        },
    },
})
